'use client'

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import Heading from '@tebuto/typography/Heading'
import { useContext, useMemo } from 'react'
import { BookingContext } from './context'
import type { EventCategory, Therapist, TherapistContactInfo } from './types'
import { EventLocation } from './types'

function formatPrice(price: string): string {
    const num = Number.parseFloat(price)
    if (Number.isNaN(num) || num === 0) return ''
    return `${num.toFixed(2).replace('.', ',')} €`
}

function formatDuration(minutes: number | undefined): string {
    if (!minutes) return ''
    if (minutes < 60) return `${minutes} Min.`
    const hours = Math.floor(minutes / 60)
    const remaining = minutes % 60
    if (remaining === 0) return `${hours} Std.`
    return `${hours} Std. ${remaining} Min.`
}

function locationLabel(location: EventLocation): string {
    switch (location) {
        case EventLocation.ONSITE:
            return 'Vor Ort'
        case EventLocation.VIRTUAL:
            return 'Online'
        case EventLocation.NOT_FIXED:
            return 'Vor Ort / Online'
    }
}

function CategoryCard({ category, onSelect }: { category: EventCategory; onSelect: (categoryId: number) => void }) {
    const price = formatPrice(category.price)
    const duration = formatDuration(category.duration)

    return (
        <button
            type="button"
            onClick={() => onSelect(category.id)}
            className="w-full text-left py-5 px-3 hover:widget-bg-muted transition-colors group cursor-pointer rounded-none hover:rounded-lg"
            data-testid={`category-card-${category.id}`}
        >
            <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[11px] widget-text-secondary mb-1">
                        {duration && <span>{duration}</span>}
                        {duration && (price || category.location) && <span>·</span>}
                        {price && <span>{price}</span>}
                        {price && category.location && <span>·</span>}
                        <span>{locationLabel(category.location)}</span>
                    </div>
                    <h3 className="text-base font-semibold widget-text-primary">{category.name}</h3>
                    {category.description && (
                        <div
                            className="widget-category-description widget-text-secondary mt-1.5 leading-relaxed [&_p]:m-0 [&_ul]:mt-1 [&_ul]:mb-0 [&_ul]:pl-4 [&_ul]:list-disc [&_ol]:mt-1 [&_ol]:mb-0 [&_ol]:pl-4 [&_ol]:list-decimal [&_li]:m-0 [&_li]:pl-0.5"
                            style={{ fontSize: '14px' }}
                            dangerouslySetInnerHTML={{ __html: category.description }}
                        />
                    )}
                </div>
                <svg
                    className="size-4 shrink-0 widget-text-secondary opacity-30 group-hover:translate-x-0.5 group-hover:opacity-60 transition-all"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </div>
        </button>
    )
}

function TherapistProfile({ therapist, profileUrl }: { therapist: Therapist; profileUrl?: string }) {
    const { t } = useComponentTranslation()

    const displayName = useMemo(() => {
        const parts: string[] = []
        if (therapist.title) parts.push(therapist.title)
        if (therapist.firstName) parts.push(therapist.firstName)
        if (therapist.lastName) parts.push(therapist.lastName)
        return parts.length > 0 ? parts.join(' ') : therapist.name
    }, [therapist])

    const contactItems = useMemo(() => {
        if (!therapist.contactInfos) return []
        return therapist.contactInfos.filter((c: TherapistContactInfo) => c.type === 'email' || c.type === 'phone')
    }, [therapist.contactInfos])

    return (
        <div
            className="flex items-start gap-4 p-4 border widget-border rounded-lg mb-4"
            data-testid="therapist-profile"
        >
            {therapist.image && (
                <img src={therapist.image} alt={displayName} className="size-14 rounded-full object-cover shrink-0" />
            )}
            <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold widget-text-primary">{displayName}</h3>
                {therapist.address?.city?.name && (
                    <p className="text-xs widget-text-secondary mt-0.5">{therapist.address.city.name}</p>
                )}
                {contactItems.length > 0 && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        {contactItems.map((contact: TherapistContactInfo) => (
                            <a
                                key={`${contact.type}-${contact.value}`}
                                href={contact.type === 'email' ? `mailto:${contact.value}` : `tel:${contact.value}`}
                                className="text-xs widget-text-secondary hover:widget-text-primary underline transition-colors"
                            >
                                {contact.value}
                            </a>
                        ))}
                    </div>
                )}
                {profileUrl && (
                    <a
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-xs mt-2 widget-text-primary-color-600 hover:widget-text-primary-color-700 underline transition-colors"
                    >
                        {t('booking.categorySelection.viewProfile')}
                    </a>
                )}
            </div>
        </div>
    )
}

export default function CategorySelection() {
    const {
        categories,
        therapist,
        setSelectedCategoryId,
        setState,
        setNavigationDirection,
        showTherapistProfile,
        profileUrl,
        categorySelectionHeading
    } = useContext(BookingContext)

    const sortedCategories = useMemo(
        () => Object.values(categories).sort((a, b) => a.name.localeCompare(b.name, 'de')),
        [categories]
    )

    function handleCategorySelect(categoryId: number) {
        setSelectedCategoryId(categoryId)
        setNavigationDirection('forward')
        setState('calendar')
    }

    return (
        <div className="flex flex-col flex-1 min-h-0" data-testid="category-selection">
            {showTherapistProfile && <TherapistProfile therapist={therapist} profileUrl={profileUrl} />}
            {categorySelectionHeading && (
                <Heading level={4} className="text-lg widget-text-primary font-semibold mb-4">
                    {categorySelectionHeading}
                </Heading>
            )}
            <div className="flex flex-col overflow-y-auto flex-1 min-h-0 widget-scrollbar widget-category-list">
                {sortedCategories.map((category) => (
                    <CategoryCard key={category.id} category={category} onSelect={handleCategorySelect} />
                ))}
            </div>
        </div>
    )
}
